import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  type User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  GoogleAuthProvider,
  type AuthError,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/lib/firebase';
import { getFirebaseAuthErrorMessage } from '@readlater/shared';

// Enable mock mode when VITE_MOCK_API is set
const MOCK_MODE = import.meta.env.VITE_MOCK_API === 'true';

// Dev auto-login: real Firebase auth with seeded credentials
const DEV_EMAIL = import.meta.env.VITE_DEV_EMAIL;
const DEV_PASSWORD = import.meta.env.VITE_DEV_PASSWORD;
const DEV_AUTO_LOGIN = import.meta.env.DEV && !MOCK_MODE && !!DEV_EMAIL && !!DEV_PASSWORD;

// Mock user for development without Firebase
const MOCK_USER = {
  uid: 'mock-user-123',
  email: 'demo@readlater.app',
  displayName: 'Demo User',
  photoURL: null,
  emailVerified: true,
  getIdToken: async () => 'mock-token-123',
} as unknown as User;

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getAuthErrorMessage(error: AuthError): string {
  return getFirebaseAuthErrorMessage(error.code, error.message);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(MOCK_MODE ? MOCK_USER : null);
  const [loading, setLoading] = useState(!MOCK_MODE);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // In mock mode, user is already set
    if (MOCK_MODE) {
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth) {
      setLoading(false);
      setError('Firebase is not configured. Check environment variables.');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    if (DEV_AUTO_LOGIN && !auth.currentUser) {
      signInWithEmailAndPassword(auth, DEV_EMAIL, DEV_PASSWORD).catch((err) =>
        console.error('[dev-auto-login]', err)
      );
    }

    return unsubscribe;
  }, []);

  const clearError = () => setError(null);

  const signIn = async (email: string, password: string) => {
    if (MOCK_MODE) {
      setUser(MOCK_USER);
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth) throw new Error('Firebase not configured');

    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = getAuthErrorMessage(err as AuthError);
      setError(message);
      throw new Error(message);
    }
  };

  const signUp = async (email: string, password: string) => {
    if (MOCK_MODE) {
      setUser(MOCK_USER);
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth) throw new Error('Firebase not configured');

    setError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = getAuthErrorMessage(err as AuthError);
      setError(message);
      throw new Error(message);
    }
  };

  const signInWithGoogle = async () => {
    if (MOCK_MODE) {
      setUser(MOCK_USER);
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth) throw new Error('Firebase not configured');

    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      const message = getAuthErrorMessage(err as AuthError);
      setError(message);
      throw new Error(message);
    }
  };

  const signOut = async () => {
    if (MOCK_MODE) {
      setUser(null);
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth) throw new Error('Firebase not configured');

    setError(null);
    await firebaseSignOut(auth);
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!user) return null;
    return user.getIdToken();
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    getIdToken,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get the current user (null if not authenticated)
 */
export function useUser() {
  const { user } = useAuth();
  return user;
}

/**
 * Hook to get Firebase ID token for API requests
 */
export function useIdToken() {
  const { getIdToken } = useAuth();
  return getIdToken;
}
